import { ChangeEvent, useEffect, useState } from "react";
import { ethers } from "ethers";
import JicFloatingWidget from "./jicFloatingWidget";
import { describeFace, loadFaceRecognitionModels } from "../lib/faceRecognition";
import { getJicToken, getJicUser, loginJic, onJicChange, type JicUser } from "../lib/jic";

// /create — 小白极简创世:起名 → 选类型 → 一个按钮。
// 池子默认 = 你自己的 JIC 账户;创建用 10 LOVE(9 留在你账户,1 烧掉)。
const JIC_URL = "https://u.jic.io";
const LOVE_ADDRESS = "0x16A7F8CfAD687A87183fCbd1dF7aF09dce05D357";
const DRAFT_KEY = "face3:genesis-draft";

type Draft = { slug: string; kind: "relay" | "prereg"; txHash?: string };

export default function AnchorGenesis() {
  const [user, setUser] = useState<JicUser | null>(() => getJicUser());
  const [draft, setDraft] = useState<Draft>(() => {
    try {
      return { slug: "", kind: "relay", ...(JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "{}") as Partial<Draft>) };
    } catch {
      return { slug: "", kind: "relay" };
    }
  });
  const [balance, setBalance] = useState<number | null>(null);
  const [phase, setPhase] = useState<"form" | "minting" | "minted">("form");
  const [status, setStatus] = useState("");
  const [templates, setTemplates] = useState(0);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => onJicChange(setUser), []);
  useEffect(() => {
    loadFaceRecognitionModels().catch(() => undefined);
  }, []);

  const myAddress = user?.sub && /^0x[a-fA-F0-9]{40}$/.test(user.sub) ? user.sub.toLowerCase() : null;

  // 我的 LOVE 余额(池子=自己账户,需要 ≥ 10:9 留下 + 1 烧掉)
  useEffect(() => {
    if (!myAddress) return;
    const provider = new ethers.JsonRpcProvider("https://mainnet.base.org", 8453, { staticNetwork: true });
    const love = new ethers.Contract(LOVE_ADDRESS, ["function balanceOf(address) view returns (uint256)"], provider);
    love.balanceOf(myAddress)
      .then((raw: bigint) => setBalance(Number(ethers.formatUnits(raw, 18))))
      .catch(() => setBalance(null));
  }, [myAddress]);

  const saveDraft = (next: Draft) => {
    setDraft(next);
    try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(next)); } catch { /* 忽略 */ }
  };

  const slugOk = /^[a-z0-9_-]{1,32}$/.test(draft.slug);

  const submitGenesis = async (payload: Draft) => {
    const token = getJicToken();
    if (!token || !myAddress) { setStatus("请先登录"); return; }
    setPhase("minting");
    setStatus("正在确认并创建…");
    try {
      const response = await fetch(`${JIC_URL}/v1/face/anchor/genesis`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ slug: payload.slug, label: payload.kind === "relay" ? `${payload.slug} · LOVE 中转站` : `${payload.slug} · 等待认领`, kind: payload.kind, poolAddress: myAddress, txHash: payload.txHash }),
      });
      const data = (await response.json()) as { ok?: boolean; error?: string; balance?: string };
      if (!data.ok) {
        setPhase("form");
        setStatus(
          data.error === "pool underfunded" ? `你的账户还需要至少 9 LOVE(现在 ${Number(data.balance || 0).toFixed(2)})`
          : data.error === "slug taken" || data.error === "slug owned on-chain" ? "这个名字被占了,换一个"
          : "没成功,再试一次"
        );
        return;
      }
      setPhase("minted");
      setStatus("");
      try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* 忽略 */ }
    } catch {
      setPhase("form");
      setStatus("网络不稳,你烧的 LOVE 没浪费,点下面的按钮重试");
    }
  };

  // 指挥舱燃烧回跳:#love_tx=<hash> → 自动创建
  useEffect(() => {
    const hit = window.location.hash.match(/^#love_tx=(0x[0-9a-fA-F]{64})$/);
    if (!hit) return;
    window.history.replaceState(null, "", window.location.pathname);
    const next = { ...draft, txHash: hit[1] };
    saveDraft(next);
    void submitGenesis(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = () => {
    if (!slugOk) { setStatus("名字用小写字母或数字,比如 mylove"); return; }
    if (!getJicToken() || !myAddress) { loginJic({ clearLocal: false }); return; }
    if (balance !== null && balance < 10) {
      setStatus(`创建需要账户里有 10 LOVE(现在 ${balance.toFixed(2)}),先去充一点`);
      return;
    }
    const params = new URLSearchParams({
      pay_to: "0x000000000000000000000000000000000000dEaD",
      pay_amount: "1",
      pay_token: "LOVE",
      pay_note: `创建 ${draft.slug}`,
      pay_return: window.location.href.split("#")[0],
    });
    window.location.href = `https://u.jic.io/u?${params.toString()}`;
  };

  // 拍照/选图 → 模板入库
  const onTemplatePhoto = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setEnrolling(true);
    setStatus("正在认图里的脸…");
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = async () => {
      try {
        const descriptor = await describeFace(image);
        if (!descriptor) throw new Error("图里没认出清晰的脸,换一张");
        const token = getJicToken();
        const response = await fetch(`${JIC_URL}/v1/face/anchor/${draft.slug}/descriptor`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ descriptor }),
        });
        const data = (await response.json()) as { ok?: boolean; anchor?: { templates: number }; error?: string };
        if (!data.ok) throw new Error("没存上,再试一次");
        setTemplates(data.anchor?.templates || templates + 1);
        setStatus(`✓ 已录入 ${data.anchor?.templates ?? templates + 1} 张,多录几张认得更准`);
      } catch (error) {
        setStatus(error instanceof Error ? error.message : "没认出来,换一张");
      } finally {
        URL.revokeObjectURL(objectUrl);
        event.target.value = "";
        setEnrolling(false);
      }
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setEnrolling(false);
      setStatus("图片打不开,换一张");
    };
    image.src = objectUrl;
  };

  return (
    <main className="fs-page">
      <div className="face3-aurora face3-aurora-one" />
      <div className="face3-aurora face3-aurora-two" />
      <section className="fs-home" style={{ justifyContent: "flex-start", paddingTop: 60 }}>
        <div className="fs-brand"><span>⚓</span> 创建锚点</div>

        {phase !== "minted" && (
          <>
            <h1 className="fs-title">给一张脸,开一个池</h1>
            <p className="fs-msg" style={{ maxWidth: 320 }}>圣像、偶像、任何值得被记住的脸。别人扫到它,LOVE 就会流进来。</p>

            <input
              className="fs-big-input"
              value={draft.slug}
              onChange={(event) => saveDraft({ ...draft, slug: event.target.value.toLowerCase().trim() })}
              placeholder="起个名字,如 mary"
              autoCapitalize="off"
              spellCheck={false}
            />
            {slugOk && <p className="fs-foot">它的网址:jic.io/{draft.slug}</p>}

            <div className="fs-kind">
              <button type="button" className={draft.kind === "relay" ? "on" : ""} onClick={() => saveDraft({ ...draft, kind: "relay" })}>
                ⛪<b>属于大家</b><small>圣像 / 符号,LOVE 进公共池</small>
              </button>
              <button type="button" className={draft.kind === "prereg" ? "on" : ""} onClick={() => saveDraft({ ...draft, kind: "prereg" })}>
                ◇<b>帮 TA 占位</b><small>偶像 / 名人,正主来了归 TA</small>
              </button>
            </div>

            <button type="button" className="fs-pay-btn" style={{ maxWidth: 340 }} onClick={create} disabled={phase === "minting"}>
              {phase === "minting" ? "创建中…" : user && getJicToken() ? "🔥 用 10 LOVE 创建" : "登录后创建"}
            </button>
            <p className="fs-foot">9 LOVE 留在你账户当池子,1 LOVE 烧掉{balance !== null ? ` · 你有 ${balance.toFixed(2)} LOVE` : ""}</p>
            {draft.txHash && phase === "form" && (
              <button type="button" className="fs-link" onClick={() => submitGenesis(draft)}>已烧过 LOVE?点这里重试创建</button>
            )}
          </>
        )}

        {phase === "minted" && (
          <>
            <h1 className="fs-title">⚓ {draft.slug} 建好了</h1>
            <p className="fs-msg">最后一步:传 TA 的照片,别人才能扫到</p>
            <label className="fs-pay-btn" style={{ maxWidth: 340, display: "grid", placeItems: "center", cursor: "pointer" }}>
              {enrolling ? "认脸中…" : templates > 0 ? `再传一张(已有 ${templates} 张)` : "📸 传一张 TA 的照片"}
              <input type="file" accept="image/*" onChange={onTemplatePhoto} hidden />
            </label>
            {templates > 0 && (
              <a className="fs-link" href={`/anchor/${draft.slug}`} style={{ color: "#58e8ff", fontWeight: 800 }}>
                完成,去看 {draft.slug} 的主页 →
              </a>
            )}
          </>
        )}

        {status && <p className="fs-msg">{status}</p>}
      </section>
      <JicFloatingWidget />
    </main>
  );
}
